import { ReactElement, useCallback, useMemo } from 'react';
import { Typography } from '@mui/material';
import Map, {
  NavigationControl,
  FullscreenControl,
  Source,
  Layer,
} from 'react-map-gl';
import mapboxgl from 'mapbox-gl';

import { AdminReportHeatmapCoords } from '~services/admin/report/types';
import { TOKEN } from '~utils/mapbox';
import { controlLocale } from '~constants/mapControlLocale';
import ModeSateliteControl from '~components/MapCustomControl/ModeSateliteControl';

import { ReportsHeatmapSectionMapProps } from './types';
import { ReportsHeatmapSectionMapContainer } from './styles';

const ReportsHeatmapSectionMap = ({
  visits,
}: ReportsHeatmapSectionMapProps): ReactElement => {
  const getCurrentCoords = useCallback(
    (visit: AdminReportHeatmapCoords) =>
      new mapboxgl.LngLat(visit.visitCoordsLong, visit.visitCoordsLat),
    [],
  );

  const bounds = useMemo(() => {
    const boundsInstance = new mapboxgl.LngLatBounds();

    visits.forEach((visit) => {
      boundsInstance.extend(getCurrentCoords(visit));
    });

    return boundsInstance;
  }, [visits, getCurrentCoords]);

  const sourceData = useMemo(() => {
    const features: GeoJSON.Feature[] = visits.map((visit) => ({
      type: 'Feature',
      properties: { dbh: 0 },
      geometry: {
        type: 'Point',
        coordinates: getCurrentCoords(visit).toArray(),
      },
    }));

    const featureColletion: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    };

    const geojsonData: mapboxgl.GeoJSONSourceRaw = {
      type: 'geojson',
      data: featureColletion,
    };

    return geojsonData;
  }, [visits, getCurrentCoords]);

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        El mapa de calor muestra con color más cálido las zonas donde la
        agrupación de pedidos es mayor. Se listan los pedidos de los últimos 60
        días.
      </Typography>

      <ReportsHeatmapSectionMapContainer>
        <Map
          mapboxAccessToken={TOKEN}
          mapStyle="mapbox://styles/mapbox/light-v10"
          initialViewState={{
            bounds,
            fitBoundsOptions: {
              padding: 100,
              maxZoom: 12,
            },
          }}
          attributionControl={false}
          locale={controlLocale}
        >
          <FullscreenControl position="bottom-left" />
          <NavigationControl position="bottom-left" showCompass={false} />
          <ModeSateliteControl position="bottom-left" />

          <Source {...sourceData}>
            <Layer
              id="trees-heat"
              type="heatmap"
              paint={{
                'heatmap-intensity': {
                  stops: [
                    [11, 1],
                    [15, 3],
                  ],
                },
                'heatmap-radius': {
                  stops: [
                    [11, 15],
                    [15, 20],
                  ],
                },
                'heatmap-opacity': {
                  default: 1,
                  stops: [[14, 0.5]],
                },
              }}
            />
          </Source>
        </Map>
      </ReportsHeatmapSectionMapContainer>
    </>
  );
};

export default ReportsHeatmapSectionMap;
